import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { SCHEDULES_QUERY, SCHEDULE_GROUPS_QUERY } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
// Direct module paths, NOT barrels — see the note in `useAssetsPrefetch`.
import { resolveItemPageSize, resolveFolderPageSize } from '@/components/shared/FolderItemList/dev';
import { useScheduleSortStore } from '@/features/schedules/stores/schedule-sort-store';

/**
 * `SCHEDULE_PAGE_SIZE` from `features/schedules/constants` — duplicated rather than imported.
 * That module pulls in `rrule` and the whole schedule constant set, and this hook is mounted from
 * AppShell, so importing it would drag all of that into the MAIN bundle to obtain one integer.
 * `prefetch-cache-key-parity.test.ts` asserts this stays equal to the real constant.
 */
const SCHEDULE_PAGE_SIZE = 20;

/**
 * Idle-time prefetch for the Schedules list. See `useAssetsPrefetch` for the full rationale.
 *
 * #1229 specified Schedules as chunk-only, on the grounds that "date-range params make data
 * prefetch unreliable". That does not apply to the LIST query: `schedulesVars`
 * (`useScheduleList.ts:298`) is `{ first, teamId, query, sort }` — structurally identical to
 * Assets and Playlists, with no date range. The date range belongs to the calendar DETAIL panel,
 * which loads a user-selected schedule via `useScheduleDetail` and is correctly not prefetchable.
 * `SchedulesPage` renders the sidebar list unconditionally, so this query always fires on a cold
 * visit. Prefetching it was approved on 2026-09-14 as a deliberate deviation from the ticket.
 *
 * Note the page size is **20**, not the 50 used by Assets/Playlists.
 *
 * Sort comes from a zustand `persist` store (the only true `persist` user of the three); with the
 * default localStorage adapter rehydration is synchronous at store creation, so there is no race.
 */
export function useSchedulesPrefetch(): void {
  const client = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const sortField = useScheduleSortStore((s) => s.sortField);
  const sortDirection = useScheduleSortStore((s) => s.sortDirection);

  useEffect(() => {
    if (!activeTeamId) return;

    const run = () => {
      const sort = { [sortField]: sortDirection };

      client
        .query({
          query: SCHEDULES_QUERY,
          variables: {
            first: resolveItemPageSize(SCHEDULE_PAGE_SIZE),
            teamId: activeTeamId,
            // Root: `isValidPath` is false, so `path` is null and `teamId` is not set on the
            // query (useScheduleList.ts:129-133).
            query: { path: null },
            sort,
            // NO extendParams key at defaults — see `usePlaylistsPrefetch`.
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {
          // Best-effort — don't surface errors to the user.
        });

      client
        .query({
          query: SCHEDULE_GROUPS_QUERY,
          variables: {
            first: resolveFolderPageSize(),
            teamId: activeTeamId,
            query: { type: 'SCHEDULE', path: null },
            sort,
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      setTimeout(run, 2000);
    }
  }, [client, activeTeamId, sortField, sortDirection]);
}
